<template>
  <div class="flex items-center gap-2">
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <Button
          variant="outline"
          size="sm"
          type="button"
          class="h-8 justify-between gap-2"
          :loading="isUpdating"
        >
          <span class="truncate"><span v-if="showLabel" class="text-sm text-muted-foreground">
            Priority:
          </span>{{ priorityLabel }}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" class="w-56">
        <DropdownMenuLabel>Change Priority:</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem :disabled="isUpdating" @select="setPriority('low')">
          Low
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="setPriority('standard')">
          Standard
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="setPriority('high')">
          High
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { toast } from 'vue-sonner'
import { Button } from '../../../../../../../../resources/js/components/ui/button/index.js'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../../../../../resources/js/components/ui/dropdown-menu/index.js'
import { useTaskTemplateStore } from '../../../stores/taskTemplateStore.js'

const PRIORITY_LABELS = {
  low: 'Low',
  standard: 'Standard',
  high: 'High',
}

const props = defineProps({
  taskTemplate: {
    type: Object,
    required: true,
  },
  value: {
    type: [String, Object],
    default: null,
  },
  context: {
    type: String,
    default: 'task-template-show',
  },
  showLabel: {
    type: Boolean,
    default: false,
  },
})

const taskTemplateStore = useTaskTemplateStore(props.context)
const taskTemplateId = computed(() => Number(props.taskTemplate?.id))
const isUpdating = computed(() => taskTemplateStore.isUpdatingPriority(taskTemplateId.value))
const normalizedPriority = computed(() => {
  if (typeof props.value === 'string') {
    return props.value
  }

  if (props.value && typeof props.value === 'object' && 'value' in props.value) {
    return String(props.value.value)
  }

  return null
})
const priorityLabel = computed(() => PRIORITY_LABELS[normalizedPriority.value] ?? 'No priority')

const setPriority = async (priority) => {
  if (!Number.isInteger(taskTemplateId.value) || taskTemplateId.value <= 0) {
    return
  }

  if (normalizedPriority.value === priority) {
    return
  }

  try {
    await taskTemplateStore.updatePriority(taskTemplateId.value, priority)
    toast.success(`Priority set to ${PRIORITY_LABELS[priority]}.`)
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.priority?.[0]
      || 'Unable to update the priority.'

    toast.error(errorMessage)
  }
}
</script>
